// Página: Cenografia
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getWhatsAppLink } from "../components/whatsapp/getWhatsAppLink";
import GalleryCarousel from "../components/portfolio/GalleryCarousel";
import Lightbox from "../components/portfolio/Lightbox";
import { portfolioMedia } from "../data/portfolioMedia";

function Cenografia() {
  const title = "Cenografia para eventos, feiras e ativações | Cenovix";
  const description =
    "Cenografia premium para eventos corporativos, feiras, stands e ativações. Projeto, produção e acabamento com leitura de marca e resultado em foto e vídeo.";
  const url = "https://www.cenovix.com.br/cenografia";
  const ogImage = "https://www.cenovix.com.br/images/og-cenovix.jpg";

  const waLink = getWhatsAppLink("/cenografia");

  const [videoLbOpen, setVideoLbOpen] = useState(false);

  // Vídeo
  const videoSrc = "/videos/cenografia/01.mp4";
  const videoPoster =
    "/images/portfolio/posters/cenografia-video.jpeg"; // capa do vídeo

  const videoLbItems = [{ src: videoSrc, poster: videoPoster, type: "video" }];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Cenografia para eventos, feiras e ativações",
    description,
    provider: {
      "@type": "LocalBusiness",
      name: "Cenovix",
      url: "https://www.cenovix.com.br",
      areaServed: "BR",
    },
    url,
  };

  return (
    <div className="cenografia">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow,max-image-preview:large" />

        {/* Compartilhar */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenovix" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />
        <meta
          property="og:image:alt"
          content="Cenografia premium Cenovix para eventos e ativações"
        />

        {/* Compartilhar */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="cenografia-hero" aria-label="Cenografia Cenovix">
        <h1>Cenografia de presença e impacto</h1>

        <p className="hero-subtitle">
          Do conceito à execução. Projetos sob medida para{" "}
          <strong>eventos corporativos</strong>, <strong>feiras</strong> e{" "}
          <strong>ativações</strong> — com acabamento e leitura de marca.
        </p>
      </section>

      {/* VÍDEO */}
      <section aria-label="Vídeo de cenografia">
        <h2>Vídeo do que podemos fazer no seu evento</h2>
        <p>
          Um vídeo curto mostra melhor a <strong>presença</strong>, o{" "}
          <strong>acabamento</strong> e a <strong>experiência</strong>.
        </p>

        <div
          className="video-card"
          role="button"
          tabIndex={0}
          aria-label="Abrir vídeo em tela cheia"
          onClick={() => setVideoLbOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setVideoLbOpen(true);
          }}
        >
          <video
            className="video-card__media"
            src={videoSrc}
            poster={videoPoster}
            playsInline
            preload="metadata"
          />
        </div>
      </section>

      {/* PROVAS VISUAIS */}
      <section className="visual-editorial" aria-label="Projetos de cenografia">
        <GalleryCarousel
          items={portfolioMedia.cenografia.map(({ caption, ...rest }) => rest)}
          ariaLabel="Galeria de cenografia — carrossel"
        />

        <div style={{ marginTop: 18 }}>
          <Link
            to="/portfolio#cenografia"
            className="btn-portfolio"
            aria-label="Ver mais projetos de cenografia no portfólio"
          >
            Veja mais no portfólio
          </Link>
        </div>
      </section>

      {/* O QUE ENTREGAMOS */}
      <section>
        <h2>O que a Cenovix entrega em cenografia</h2>

        <div className="solution-cards">
          <div className="solution-card">
            <h3>Conceito</h3>
            <p>
              Transformamos ideia vaga em direção criativa clara — com referências
              e proposta alinhadas ao objetivo do evento.
            </p>
          </div>

          <div className="solution-card">
            <h3>Execução</h3>
            <p>
              Produção completa com time: marcenaria, serralheria, elétrica, com
              acabamento e montagem no local.
            </p>
          </div>

          <div className="solution-card">
            <h3>Entrega</h3>
            <p>
              Controle de prazo e qualidade para o resultado final ficar coerente
              com o que foi aprovado.
            </p>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section>
        <h2>Como funciona</h2>

        <div className="process-flow" role="list">
          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Briefing</h3>
              <p>Entendemos objetivo, público, local e referências.</p>
            </div>
          </div>

          <div className="process-arrow" aria-hidden="true">
            →
          </div>

          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Proposta</h3>
              <p>Apresentamos direção criativa, materiais e orçamento.</p>
            </div>
          </div>

          <div className="process-arrow" aria-hidden="true">
            →
          </div>

          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Produção</h3>
              <p>Construção e preparação com controle de qualidade.</p>
            </div>
          </div>

          <div className="process-arrow" aria-hidden="true">
            →
          </div>

          <div className="process-step" role="listitem">
            <div className="process-step__content">
              <h3>Montagem</h3>
              <p>Entrega no local com acabamento e segurança.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ONDE ATUAMOS */}
      <section aria-label="Onde atuamos">
        <h2>Onde atuamos</h2>

        <div className="solution-cards">
          <div className="solution-card">
            <h3>Vila Velha • Vitória • Grande Vitória</h3>
            <p>
              Atuamos com cenografia e iluminação cênica em Vila Velha, Vitória e
              Grande Vitória, atendendo eventos corporativos e sociais em todo o
              Espírito Santo.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <h2>Quer uma cenografia com identidade e acabamento de verdade?</h2>

        <p>
          Me mande no WhatsApp a <strong>data</strong>, o <strong>local</strong>, o{" "}
          <strong>tipo de evento</strong> e referências (se tiver).
        </p>

        <a
          className="cta"
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Falar com a Cenovix no WhatsApp"
        >
          Pedir orçamento no WhatsApp
        </a>
      </section>

      <Lightbox
        open={videoLbOpen}
        items={videoLbItems}
        index={0}
        onClose={() => setVideoLbOpen(false)}
      />

    </div>
  );
}

export default Cenografia;
